import type {Invite} from './types';

import {membershipStore} from '../../../api/membership';
import {currentUserStore} from '../../../api/user';
import {alert} from '../../../components/alert';
import {isInvite} from '../../../utils/entity/invite/isInvite';
import {isIamCoach} from '../../../utils/entity/isIamCoach';
import {isMeCoachForEntity} from '../../../utils/entity/isMeCoachForEntity';
import {isMembership} from '../../../utils/entity/membership/isMembership';
import {AppError} from '../../../utils/errors/app';
import {logger} from '../../../utils/logger';
import {type AsyncStrategy, COMMAND, isCancelCommand} from '../utils';
import {DeleteInviteStrategyFactory} from './inviteDelete/DeleteInviteStrategyFactory';
import {membershipDeclineReasonConfirm} from './inviteDelete/membershipDeclineReasonConfirm';

type Config = {
  invite: Invite;
};

export {inviteDelete};

const strategy: AsyncStrategy<Config> = async (roles, config) => {
  const {invite} = config;

  if (isMeCoachForInvite(invite)) {
    return roles.coach(config);
  } else {
    return roles.client(config);
  }
};

async function inviteDelete(config: Config) {
  return strategy({client: asClient, coach: asCoach}, config);
}

inviteDelete.asCoach = asCoach;
inviteDelete.asClient = asClient;

async function asClient({invite}: Config) {
  if (!isMembership(invite)) {
    throw new AppError('Client can delete only proposed memberships');
  }

  const confirmResult = await membershipDeclineReasonConfirm();

  if (isCancelCommand(confirmResult)) {
    return COMMAND.CANCEL;
  }

  const {reason} = confirmResult;

  try {
    await membershipStore.client.decline(invite, {
      decline_reason: reason || null,
    });
  } catch (e) {
    logger.error(e);

    alert.error(e);
  }
}

async function asCoach({invite}: Config) {
  try {
    const isConfirmed = await alert.confirm();

    if (!isConfirmed) {
      return COMMAND.CANCEL;
    }

    await DeleteInviteStrategyFactory.createStrategy(invite).deleteInvite();
  } catch (error) {
    logger.error(error);

    throw error;
  }
}

function isMeCoachForInvite(invite: Invite): boolean {
  if (!isIamCoach()) {
    return false;
  }

  const user = currentUserStore.user;

  if (isInvite(invite)) {
    return invite.coach_id === user?._id;
  }

  if (isMembership(invite)) {
    return isMeCoachForEntity(invite);
  }

  throw new AppError('Invalid invite type');
}
